// src/pages/UserManagement/UserDetailPanel.tsx
import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Divider,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Grid,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  PhotoCamera as PhotoCameraIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Mail as MailIcon,
  VpnKey as VpnKeyIcon
} from '@mui/icons-material';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useNavigate } from 'react-router-dom';

interface User {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  createdAt?: Date;
  lastLogin?: Date;
  isAdmin?: boolean;
  isActive?: boolean;
  role: string;
}

interface Trip {
  id: string;
  title: string;
  startDate: Date;
  endDate: Date;
  createdBy: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-detail-tabpanel-${index}`}
      aria-labelledby={`user-detail-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

interface UserDetailPanelProps {
  userId: string;
}

const UserDetailPanel: React.FC<UserDetailPanelProps> = ({ userId }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    isAdmin: false,
    isActive: true
  });
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [userTrips, setUserTrips] = useState<Trip[]>([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const navigate = useNavigate();
  
  // Implement the onClose function
  const onClose = () => {
    // Navigate back to the main user list
    navigate('/users');
  };
  
  // Implement the onUpdate function
  const onUpdate = async (userId: string, data: Partial<User>) => {
    try {
      // Update the user document in Firestore
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, data);
      
      // Update local state
      setUser(prev => prev ? { ...prev, ...data } : null);
      setUpdateSuccess(true);
      
      // Hide success message after a delay
      setTimeout(() => setUpdateSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update user');
    }
  };
  
  // Implement the onDelete function
  const onDelete = async (userId: string) => {
    try {
      // Delete the user document from Firestore
      await deleteDoc(doc(db, 'users', userId));
      
      // Navigate back to the main user list
      navigate('/users');
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user');
    }
  };
  
  // Fetch user data on component mount or when userId changes
  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        
        if (userDoc.exists()) {
          const userData = {
            uid: userDoc.id,
            ...userDoc.data()
          } as User;
          
          setUser(userData);
          
          // Initialize form data from user data
          setFormData({
            displayName: userData.displayName || '',
            email: userData.email || '',
            isAdmin: userData.role === 'owner',
            isActive: userData.isActive !== false // Default to true if not specified
          });
        } else {
          setError('User not found');
          setUser(null);
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user data');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [userId]);
  
  // Fetch user's trips
  useEffect(() => {
    fetchUserTrips();
  }, [user?.uid]);
  
  const fetchUserTrips = async () => {
    setTripsLoading(true);
    try {
      // Fetch trips created by this user
      const tripsQuery = query(
        collection(db, 'trips'),
        where('createdBy', '==', user?.uid)
      );
      
      const querySnapshot = await getDocs(tripsQuery);
      const trips: Trip[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        trips.push({
          id: doc.id,
          title: data.title,
          startDate: data.startDate.toDate(),
          endDate: data.endDate.toDate(),
          createdBy: data.createdBy
        });
      });
      
      // Also fetch trips the user is participating in
      const participantQuery = query(
        collection(db, 'tripParticipants'),
        where('userId', '==', user?.uid),
        where('status', '==', 'accepted')
      );
      
      const participantSnapshot = await getDocs(participantQuery);
      const participatedTripIds = new Set<string>();
      
      for (const doc of participantSnapshot.docs) {
        const tripId = doc.data().tripId;
        
        // Skip if we already have this trip from the created trips
        if (trips.some(t => t.id === tripId)) {
          continue;
        }
        
        participatedTripIds.add(tripId);
      }
      
      // Fetch details for participated trips
      for (const tripId of Array.from(participatedTripIds)) {
        const tripDoc = await getDoc(doc(db, 'trips', tripId));
        
        if (tripDoc.exists()) {
          const data = tripDoc.data();
          trips.push({
            id: tripDoc.id,
            title: data.title,
            startDate: data.startDate.toDate(),
            endDate: data.endDate.toDate(),
            createdBy: data.createdBy
          });
        }
      }
      
      setUserTrips(trips);
    } catch (err) {
      console.error('Error fetching user trips:', err);
      setError('Failed to load user trips');
    } finally {
      setTripsLoading(false);
    }
  };
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    
    setFormData({
      ...formData,
      [name]: name === 'isAdmin' || name === 'isActive' ? checked : value
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await onUpdate(user?.uid || '', {
        displayName: formData.displayName,
        role: formData.isAdmin ? 'owner' : 'editor', // Convert boolean to role string
        isActive: formData.isActive
        // Email changes should typically be handled differently
        // as they require auth system changes
      });
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update user details');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle password reset request
  const handlePasswordReset = async () => {
    setLoading(true);
    try {
      // In a real app, you would call an API or function to send a password reset email

      
      // Close dialog
      setResetPasswordDialogOpen(false);
    } catch (err) {
      console.error('Error requesting password reset:', err);
      setError('Failed to request password reset');
    } finally {
      setLoading(false);
    }
  };
  
  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Typography variant="body1" sx={{ mb: 2 }}>
          The requested user could not be found or there was an error loading their details.
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate('/users')}
          sx={{ mt: 2 }}
        >
          Return to User List
        </Button>
      </Box>
    );
  }
  
  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          User not found
        </Alert>
        <Typography variant="body1" sx={{ mb: 2 }}>
          The requested user could not be found in the database.
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate('/users')}
          sx={{ mt: 2 }}
        >
          Return to User List
        </Button>
      </Box>
    );
  }
  
  return (
    <Box sx={{ position: 'relative' }}>
      <IconButton
        onClick={onClose}
        sx={{ position: 'absolute', top: 8, right: 8 }}
      >
        <CloseIcon />
      </IconButton>
      
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          position: 'relative'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ mr: 3, textAlign: 'center' }}>
            <Avatar
              src={user.photoURL}
              sx={{ width: 80, height: 80, mb: 1 }}
            >
              {user.displayName.charAt(0)}
            </Avatar>
            <IconButton
              color="primary"
              aria-label="upload picture"
              component="label"
              size="small"
            >
              <input hidden accept="image/*" type="file" />
              <PhotoCameraIcon fontSize="small" />
            </IconButton>
          </Box>
          
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" gutterBottom>
              {user.displayName}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Chip
                label={user.role === 'owner' ? 'Owner' : user.role === 'editor' ? 'Editor' : user.role}
                color={user.role === 'owner' ? 'primary' : user.role === 'editor' ? 'secondary' : 'default'}
                size="small"
              />
              <Chip
                label={user.isActive ? 'Active' : 'Inactive'}
                color={user.isActive ? 'success' : 'error'}
                size="small"
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              Member since {user.createdAt ? user.createdAt.toLocaleDateString() : 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last login: {user.lastLogin ? user.lastLogin.toLocaleDateString() : 'Never'}
            </Typography>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Profile" />
          <Tab label="Trips" />
          <Tab label="Settings" />
        </Tabs>
        
        <TabPanel value={tabValue} index={0}>
          <form onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Display Name"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  disabled
                  helperText="Email cannot be changed"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isAdmin}
                        onChange={handleChange}
                        name="isAdmin"
                      />
                    }
                    label="Owner privileges"
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Owners can manage users and all content. Editors have limited access.
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={handleChange}
                        name="isActive"
                      />
                    }
                    label="Account active"
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Inactive accounts cannot log in
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => onDelete(user.uid)}
              >
                Deactivate User
              </Button>
              
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<VpnKeyIcon />}
                  onClick={() => setResetPasswordDialogOpen(true)}
                  sx={{ mr: 2 }}
                >
                  Reset Password
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={loading}
                >
                  Save Changes
                </Button>
              </Box>
            </Box>
          </form>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            User's Trips
          </Typography>
          
          {tripsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : userTrips.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              This user has not created or participated in any trips yet.
            </Typography>
          ) : (
            <List>
              {userTrips.map((trip) => (
                <ListItem key={trip.id} divider>
                  <ListItemText
                    primary={trip.title}
                    secondary={`${trip.startDate.toLocaleDateString()} - ${trip.endDate.toLocaleDateString()}`}
                  />
                  <Chip
                    label={trip.createdBy === user.uid ? 'Owner' : 'Participant'}
                    color={trip.createdBy === user.uid ? 'primary' : 'default'}
                    size="small"
                  />
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Account Settings
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Danger Zone
            </Typography>
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => onDelete(user.uid)}
              fullWidth
              sx={{ mb: 2 }}
            >
              Deactivate User Account
            </Button>
            
            <Button
              variant="outlined"
              color="warning"
              startIcon={<VpnKeyIcon />}
              onClick={() => setResetPasswordDialogOpen(true)}
              fullWidth
            >
              Force Password Reset
            </Button>
          </Box>
        </TabPanel>
      </Paper>
      
      {/* Password Reset Dialog */}
      <Dialog
        open={resetPasswordDialogOpen}
        onClose={() => setResetPasswordDialogOpen(false)}
      >
        <DialogTitle>Reset User Password</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reset the password for {user.email}?
            This will send a password reset email to the user.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handlePasswordReset}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Send Reset Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetailPanel;